import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import { type RoofFace, RoofFaceType } from "building-types/roofs/roofFace";
import {
  RoofPerimeterRailType,
  RoofRailCornerType,
  RoofRailEndCondition,
} from "building-types/roofs/roofPerimeterTypes";
import { isRoofTrimRailTypeVoid, type RoofTrimRail, type SoffitRail } from "building-types/roofs/roofTrimTypes";
import { EaveCutType } from "building-types/settings/settingsTypesEnum";
import { supportsAngledEaveReturn } from "doc/settingsTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { faceByFourPoints } from "geom/brep";
import { type ClipFaceContext, clippingNodeNew, clipFace } from "geom/bspClipping";
import { DIST_EPSILON } from "geom/core";
import {
  planeMoveAlongNormal,
  planeCopy,
  planeProjectPolyLine3,
  planeNew,
  planeNormal,
  polyLine2OnPlane,
  planeNewXY,
  planeReverse,
  planeEval,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeEval,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Move,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import {
  polyLine3OnPlane,
  polyLine3EdgeTangent,
  polyLine3EdgeCount,
  polyLine3EndPoint,
  polyLine3StartPoint,
  polyLine3ByTwoPoints,
  polyLine3Reverse,
  polyLine3EdgeSlopeInZ,
  type PolyLine3,
} from "geom/polyLine3";
import { vec2Normalize } from "geom/vec2";
import { vec3Add, vec3AngleBetween, vec3Cross } from "geom/vec3";
import type { RBush3D } from "rbush-3d";
import { roofFaceNew } from "../roofFaceNew";
import { getRoofPerimeterRailEndNormal, getRoofPerimeterRailStartNormal } from "./roofPerimeterRails";
import { getRoofTrimCornerInfo } from "./roofTrimRails";

interface BuildRoofTopExtensionOptions {
  readonly subRoof: GeneratedRoof;
  readonly soffitRail: SoffitRail;
  readonly isEnd: boolean;
  readonly extensionRoofTopFaces?: RoofFace[];
}

export function buildRoofTopExtension({
  subRoof,
  soffitRail,
  isEnd,
  extensionRoofTopFaces = [],
}: BuildRoofTopExtensionOptions): RoofFace[] {
  const { isDormer, roofPlanSettings, roofElevationPlanSettings, roofVolume } = subRoof;
  const { roofTopBSP } = roofVolume;
  const { roofPerimeterRail } = soffitRail;
  const {
    type,
    rail,
    side,
    appliedStartExtension,
    appliedEndExtension,
    overhang,
    exteriorWallOffsetWithSheathing,
    fasciaBoardThickness,
  } = roofPerimeterRail;

  const normal = isEnd
    ? getRoofPerimeterRailEndNormal(roofPerimeterRail)
    : getRoofPerimeterRailStartNormal(roofPerimeterRail);

  const signedSlope = isEnd
    ? -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1)
    : polyLine3EdgeSlopeInZ(rail, 0);
  const slope = Math.abs(signedSlope);
  const angle = Math.atan(slope);

  // take into account the slope of the rail when applying the extension
  const extension = (isEnd ? appliedEndExtension : appliedStartExtension) / Math.cos(angle);

  if (extension < DIST_EPSILON) {
    return extensionRoofTopFaces;
  }

  const { innerPlane, outerPlane, fasciaTopRail } = soffitRail;
  const roofTopOuterPlane = planeMoveAlongNormal(planeCopy(outerPlane), fasciaBoardThickness);

  const topRail3 = polyLine3OnPlane(fasciaTopRail, roofTopOuterPlane);

  if (!topRail3) {
    return extensionRoofTopFaces;
  }

  const [tx, ty, tz] = polyLine3EdgeTangent(topRail3, isEnd ? polyLine3EdgeCount(topRail3) - 1 : 0, isEnd);
  const [x0, y0, z0] = isEnd ? polyLine3EndPoint(topRail3) : polyLine3StartPoint(topRail3);
  const [x1, y1, z1] = vec3Add(x0, y0, z0, tx * extension, ty * extension, tz * extension);

  const roofOuterRail3 = polyLine3ByTwoPoints(x0, y0, z0, x1, y1, z1);
  if (!isEnd) {
    polyLine3Reverse(roofOuterRail3);
  }

  if (!rail && isEnd) {
    return extensionRoofTopFaces;
  }

  const roofInnerRail = planeProjectPolyLine3(innerPlane, roofOuterRail3);

  if (slope < DIST_EPSILON) {
    const angle = vec3AngleBetween(normal[0], normal[1], normal[2], 0, 0, 1);
    const offsetY = Math.tan(angle) * (overhang - exteriorWallOffsetWithSheathing);

    polyLine2Move(roofInnerRail, 0, offsetY);
  }

  const roofInnerRail3 = polyLine3OnPlane(roofInnerRail, innerPlane);

  // different points order for eave and rake extensions, to get the correct face orientation
  const face =
    type === RoofPerimeterRailType.EAVE
      ? faceByFourPoints(
          roofOuterRail3[3],
          roofOuterRail3[4],
          roofOuterRail3[5],
          roofOuterRail3[0],
          roofOuterRail3[1],
          roofOuterRail3[2],
          roofInnerRail3[0],
          roofInnerRail3[1],
          roofInnerRail3[2],
          roofInnerRail3[3],
          roofInnerRail3[4],
          roofInnerRail3[5],
        )
      : faceByFourPoints(
          roofOuterRail3[0],
          roofOuterRail3[1],
          roofOuterRail3[2],
          roofInnerRail3[0],
          roofInnerRail3[1],
          roofInnerRail3[2],
          roofInnerRail3[3],
          roofInnerRail3[4],
          roofInnerRail3[5],
          roofOuterRail3[3],
          roofOuterRail3[4],
          roofOuterRail3[5],
        );

  if (!face) {
    return extensionRoofTopFaces;
  }

  // clip the face to avoid going through the main roof volume
  const clipOptions: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(roofTopBSP, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const faces = clipFace({
    face,
    options: clipOptions,
  });

  // convert the face to a roof face and add it to the result
  const roofTopFaces = faces.map((face) =>
    roofFaceNew(RoofFaceType.NORMAL, side, face, isDormer, { ...roofPlanSettings, ...roofElevationPlanSettings }, true),
  );

  extensionRoofTopFaces.push(...roofTopFaces);

  return extensionRoofTopFaces;
}

export function buildEaveReturnPlaneGroups(roofTrimRail: RoofTrimRail, rtree: RBush3D): Plane[][] {
  const planeGroups: Plane[][] = [];

  if (isRoofTrimRailTypeVoid(roofTrimRail)) {
    return planeGroups;
  }

  const { roofPerimeterRail } = roofTrimRail;
  const {
    rail,
    eaveType,
    edgePlane,
    baseEdge,
    type,
    eaveCutType,
    isStartEdge,
    isEndEdge,
    prevPerimeterRail,
    nextPerimeterRail,
  } = roofPerimeterRail;

  const eaveCutTypeAtStart = prevPerimeterRail?.eaveCutType ?? eaveCutType;
  const eaveCutTypeAtEnd = nextPerimeterRail?.eaveCutType ?? eaveCutType;

  // early return
  if (eaveCutTypeAtStart !== EaveCutType.SQUARED && eaveCutTypeAtEnd !== EaveCutType.SQUARED) {
    return planeGroups;
  }

  // if this is the edge receiving the eave return...
  if (type === RoofPerimeterRailType.RAKE && (isStartEdge || isEndEdge) && supportsAngledEaveReturn[eaveType]) {
    const { uX, uY, uZ } = edgePlane;
    const [nX, nY, nZ] = planeNormal(edgePlane);
    const baseRail = polyLine2OnPlane(edgePlane, baseEdge);
    const [midX, midY] = polyLine2EdgeEval(baseRail, 0, 0.5, true);
    const signedStartSlope = polyLine3EdgeSlopeInZ(rail, 0);
    const signedEndSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);

    // build the planes for clipping the faces below on the start side (if needed)
    if (eaveCutTypeAtStart === EaveCutType.SQUARED && signedStartSlope > DIST_EPSILON) {
      const [, , z] = polyLine3StartPoint(rail);
      planeGroups.push([planeNew(0, 0, z, uX, uY, uZ, -nX, -nY, -nZ), planeNew(midX, midY, z, -nX, -nY, -nZ, 0, 0, 1)]);
    }

    // build the planes for clipping the faces below on the end side (if needed)
    if (eaveCutTypeAtEnd === EaveCutType.SQUARED && signedEndSlope > DIST_EPSILON) {
      const [, , z] = polyLine3EndPoint(rail);
      planeGroups.push([planeNew(0, 0, z, uX, uY, uZ, -nX, -nY, -nZ), planeNew(midX, midY, z, nX, nY, nZ, 0, 0, 1)]);
    }
    // if this is the edge creating the eave return...
  } else if (type === RoofPerimeterRailType.EAVE) {
    // create start mitter plane (if needed)
    const {
      type: startCornerType,
      neighborRail: startRail,
      otherPerimeterRail: startPerimeterRail,
    } = getRoofTrimCornerInfo(roofTrimRail, rtree, false, true);

    if (
      startCornerType === RoofRailCornerType.OUTSIDE &&
      startRail &&
      startPerimeterRail?.eaveCutTypeAtEnd === RoofRailEndCondition.SQUARED &&
      supportsAngledEaveReturn[startPerimeterRail?.eaveType]
    ) {
      planeGroups.push([buildEaveReturnMiterPlane(startRail, rail, false)]);
    }

    // create end mitter plane (if needed)
    const {
      type: endCornerType,
      neighborRail: endRail,
      otherPerimeterRail: endPerimeterRail,
    } = getRoofTrimCornerInfo(roofTrimRail, rtree, true, true);

    if (
      endCornerType === RoofRailCornerType.OUTSIDE &&
      endRail &&
      endPerimeterRail?.eaveCutTypeAtStart === RoofRailEndCondition.SQUARED &&
      supportsAngledEaveReturn[endPerimeterRail?.eaveType]
    ) {
      planeGroups.push([buildEaveReturnMiterPlane(rail, endRail, true)]);
    }
  }

  return planeGroups;
}

export function buildEaveReturnMiterPlane(prevRail: PolyLine3, nextRail: PolyLine3, isEnd: boolean): Plane {
  // project rails on the XY plane
  const groundPlane = planeNewXY(0, 0, 0);
  const prevRail2 = planeProjectPolyLine3(groundPlane, prevRail);
  const nextRail2 = planeProjectPolyLine3(groundPlane, nextRail);

  // get the plane origin
  const [x, y] = polyLine2End(prevRail2);

  // get the plane U vector
  const [tx0, ty0] = polyLine2EdgeTangent(prevRail2, 0);
  const [tx1, ty1] = polyLine2EdgeTangent(nextRail2, polyLine2EdgeCount(nextRail2) - 1, true);
  const [uX, uY] = vec2Normalize(tx0 + tx1, ty0 + ty1);

  // build the plane
  const plane = planeNew(x, y, 0, uX, uY, 0, 0, 0, 1);

  // reverse if needed
  if (!isEnd) {
    planeReverse(plane);
  }

  return plane;
}

export function buildTrimPlaneGroups(
  topRail: PolyLine2,
  edgePlane: Plane,
  startBoardSize: number,
  endBoardSize: number,
  signedStartSlope: number,
  signedEndSlope: number,
  hasSquareCutEave: boolean = false,
): Plane[][] {
  const planeGroups: Plane[][] = [];

  const { uX, uY, uZ } = edgePlane;
  const [nX, nY, nZ] = planeNormal(edgePlane);

  const minStartHeight = edgePlane.originZ + topRail[1] - startBoardSize;
  const minEndHeight = edgePlane.originZ + topRail[topRail.length - 1] - endBoardSize;

  const edgeCount = polyLine2EdgeCount(topRail);
  const topRail3d = polyLine2OnPlane(edgePlane, topRail);

  if (edgeCount > 1) {
    if (signedStartSlope > DIST_EPSILON) {
      const [u0, v0] = polyLine2EdgeEnd(topRail, 0);
      const [x0, y0, z0] = planeEval(edgePlane, u0, v0);

      if (hasSquareCutEave) {
        const [x, y, z] = polyLine3StartPoint(topRail3d);
        const [u2, v2, w2] = polyLine3EdgeTangent(topRail3d, 0);
        const [vX, vY, vZ] = vec3Cross(u2, v2, w2, nX, nY, nZ);

        planeGroups.push([planeNew(x, y, z, nX, nY, nZ, vX, vY, vZ), planeNew(x0, y0, z0, -nX, -nY, -nZ, 0, 0, 1)]);
      } else {
        planeGroups.push([
          planeNew(0, 0, minStartHeight, uX, uY, uZ, -nX, -nY, -nZ),
          planeNew(x0, y0, z0, -nX, -nY, -nZ, 0, 0, 1),
        ]);
      }
    }

    if (signedEndSlope > DIST_EPSILON) {
      const [u1, v1] = polyLine2EdgeStart(topRail, edgeCount - 1);
      const [x1, y1, z1] = planeEval(edgePlane, u1, v1);

      if (hasSquareCutEave) {
        const [x, y, z] = polyLine3EndPoint(topRail3d);
        const [u2, v2, w2] = polyLine3EdgeTangent(topRail3d, edgeCount - 1, true);
        const [vX, vY, vZ] = vec3Cross(u2, v2, w2, nX, nY, nZ);

        planeGroups.push([planeNew(x, y, z, nX, nY, nZ, vX, vY, vZ), planeNew(x1, y1, z1, nX, nY, nZ, 0, 0, 1)]);
      } else {
        planeGroups.push([
          planeNew(0, 0, minEndHeight, uX, uY, uZ, -nX, -nY, -nZ),
          planeNew(x1, y1, z1, nX, nY, nZ, 0, 0, 1),
        ]);
      }
    }
  } else if (signedStartSlope > DIST_EPSILON || signedEndSlope > DIST_EPSILON) {
    const minHeight = Math.min(minStartHeight, minEndHeight);

    planeGroups.push([planeNew(0, 0, minHeight, uX, uY, uZ, -nX, -nY, -nZ)]);
  }

  return planeGroups;
}
